import { useAnalytics } from "@codat/analytics";
import {
  Modal,
  SelectMenu,
  TextArea,
  TextInput,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import {
  SelectMenuItem,
  SelectMenuItemSingle,
} from "@codat/orchard-ui/dist/es/components/SelectMenu/model";
import { useQueryClient } from "@tanstack/react-query";
import { Company } from "api/schema/companies/companySchema";
import { useProducts } from "contexts/ProductsContext";
import { useAddProductToCompany } from "hooks/query/useAddProductToCompany";
import { companyKeys } from "hooks/query/useGetCompany";
import { useRemoveProductFromCompany } from "hooks/query/useRemoveProductFromCompany";
import { spendInsightsCompaniesKeys } from "hooks/query/useSpendInsightsCompanies";
import { useUpdateCompany } from "hooks/query/useUpdateCompany";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import getTryCatchErrorMessage from "utils/getTryCatchErrorMessage";
import { isValidCharacters } from "utils/regex";

import { FormData } from "../NewCompanyModal/NewCompanyModal";
import { COMPANY_DESCRIPTION_MAX_LENGTH } from "../utils/constants";
import styles from "./EditCompanyModal.module.scss";

export interface EditCompanyFormState
  extends Pick<Company, "id" | "name" | "description" | "tags" | "products"> {
  id: string;
  name: string;
}

interface EditCompanyModalProps {
  company: EditCompanyFormState;
  onSuccess: () => void;
  onClose: () => void;
}

const isSingle = (item: SelectMenuItem): item is SelectMenuItemSingle =>
  item.kind === "single";

const hasProductSelectionsChanged = (
  originalAssigned: string[],
  currentSelections: SelectMenuItemSingle[]
): boolean => {
  const currentEnabled = currentSelections
    .filter((item) => item.checked)
    .map((item) => item.id);

  if (originalAssigned.length !== currentEnabled.length) {
    return true;
  }

  return [...originalAssigned].some(
    (productKey) => !currentEnabled.includes(productKey)
  );
};

const useProductSelection = ({
  originalAssigned,
  currentSelections,
}: {
  originalAssigned: string[];
  currentSelections: SelectMenuItemSingle[];
}) => {
  const hasProductSelectionChanged = hasProductSelectionsChanged(
    originalAssigned,
    currentSelections
  );

  const addedProducts = currentSelections
    .filter((product) => !originalAssigned.some((p) => p === product.id))
    .map((p) => p.id);

  const removedProducts = originalAssigned.filter(
    (product) => !currentSelections.some((p) => p.id === product)
  );

  return { hasProductSelectionChanged, addedProducts, removedProducts };
};

const EditCompanyModal = ({
  company,
  onSuccess,
  onClose,
}: EditCompanyModalProps) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { products } = useProducts();
  const [autofocusFirstInput, setAutofocusFirstInput] = useState(true);

  const { isPending, mutateAsync: editCompany } = useUpdateCompany({
    companyId: company.id,
  });
  const { mutateAsync: addProductToCompany, isPending: isAddingProducts } =
    useAddProductToCompany({
      companyId: company.id,
    });
  const {
    mutateAsync: removeProductFromCompany,
    isPending: isRemovingProducts,
  } = useRemoveProductFromCompany({
    companyId: company.id,
  });

  const isUpdatingProducts = isAddingProducts || isRemovingProducts;
  const enabledProducts = products.filter(
    (product) => product.status === "Enabled"
  );

  const productsSelectMenuItems: SelectMenuItemSingle[] = enabledProducts.map(
    (product) => ({
      label: product.name,
      id: product.key,
      kind: "single",
      checked: product.isDefault,
    })
  );

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    trigger,
    clearErrors,
    formState: { isValid, errors },
  } = useForm<FormData>({
    defaultValues: {
      companyName: company.name,
      companyDescription: company.description,
      companyTags: JSON.stringify(company.tags, null, 2),
      assignedProducts: productsSelectMenuItems.map((item) => ({
        ...item,
        checked: company.products?.includes(item.id),
      })),
    },
    mode: "onChange",
  });

  const productOptions: SelectMenuItemSingle[] = watch("assignedProducts");

  const { addedProducts, removedProducts, hasProductSelectionChanged } =
    useProductSelection({
      originalAssigned: company.products,
      currentSelections: productOptions.filter((item) => item.checked),
    });

  const onEditCompany = async ({
    companyName,
    companyDescription,
    companyTags,
  }: FormData) => {
    const companyDetailsHaveChanged =
      companyName !== company.name ||
      companyDescription !== (company.description ?? "");
    const companyTagsHaveChanged =
      companyTags !== JSON.stringify(company.tags, null, 2);

    if (hasProductSelectionChanged) {
      const promises: Promise<unknown>[] = [];
      if (addedProducts.length) {
        promises.concat(
          addedProducts.map((product) =>
            addProductToCompany({ productKey: product })
          )
        );
      }
      if (removedProducts.length) {
        promises.concat(
          removedProducts.map((product) =>
            removeProductFromCompany({ productKey: product })
          )
        );
      }
      try {
        await Promise.all(promises);
      } catch (err) {
        addToast({
          message: `Failed to update company products: ${getTryCatchErrorMessage(
            err
          )}`,
          variant: "error",
        });
        return;
      }
    }

    if (!companyDetailsHaveChanged && !companyTagsHaveChanged) {
      onClose();
      return;
    }

    editCompany({
      name: companyName,
      description: companyDescription,
      tags: companyTags ? JSON.parse(companyTags) : {},
    })
      .then((updatedCompany) => {
        addToast({
          message: `Updated company ${updatedCompany.name}`,
          variant: "success",
        });
        queryClient.setQueryData([companyKeys.one(company.id)], updatedCompany);
        queryClient.invalidateQueries({
          queryKey: spendInsightsCompaniesKeys.all,
        });
        onSuccess();
      })
      .catch((e) => {
        addToast({
          message: `Failed to update company: ${getTryCatchErrorMessage(e)}`,
          variant: "error",
        });
      });
  };

  const handleClose = () => {
    sendEvent("Close");
    onClose();
  };

  const name = watch("companyName");

  useEffect(() => {
    setTimeout(() => setAutofocusFirstInput(false), 100);
  }, []);

  return (
    <Modal
      onClose={handleClose}
      className={styles.modal}
      isPinned
      size="small"
      data-testid="edit-company-modal"
    >
      <>
        <Modal.Header
          title="Edit company"
          data-testid="edit-company-modal"
          onClose={handleClose}
        />
        <form onSubmit={handleSubmit(onEditCompany)} className={styles.form}>
          <Modal.Body variant="dark">
            <Modal.Section variant="card" title="Company details">
              <TextInput
                label="Name"
                {...register("companyName", {
                  validate: (val) => {
                    if (!val.trim()) {
                      return "Name is required";
                    }
                    if (
                      (Boolean(val) && !val?.trim()) ||
                      !isValidCharacters(val)
                    ) {
                      return "Name contains invalid characters";
                    }
                  },
                })}
                data-testid="edit-company-name-input"
                data-autofocus={autofocusFirstInput}
                error={!!errors?.companyName?.message}
                errorMessage={errors?.companyName?.message}
              />
              <TextArea
                label="Description"
                showCharacterCount
                placeholder="Add a description to this company"
                maxLength={COMPANY_DESCRIPTION_MAX_LENGTH}
                className={styles.description}
                containerClassName={styles.optional}
                {...register("companyDescription", {
                  validate: (val) => {
                    if ((!!val && !val?.trim()) || !isValidCharacters(val)) {
                      return "Description contains invalid characters";
                    }
                  },
                })}
                data-testid="new-company-description-input"
                error={!!errors?.companyDescription?.message}
                errorMessage={errors?.companyDescription?.message}
              />
            </Modal.Section>
            <Modal.Section variant="card" title="Company tags">
              <TextArea
                label="Tags"
                placeholder={`Add tags to this company in JSON format e.g. { "region": "us", "label": "testing" }`}
                className={styles.description}
                containerClassName={styles.optional}
                {...register("companyTags", {
                  validate: (val) => {
                    if (val) {
                      try {
                        JSON.parse(val);
                      } catch (e) {
                        return "Must be valid JSON";
                      }
                    }
                  },
                })}
                data-testid="new-company-tags-input"
                error={!!errors?.companyTags?.message}
                errorMessage={errors?.companyTags?.message}
              />
            </Modal.Section>
            <Modal.Section variant="card" title="Company products">
              {enabledProducts.length === 1 ? (
                <Typography
                  as="div"
                  variant="body_md"
                  className={styles.selectedProducts}
                >
                  {
                    enabledProducts.find((p) => company.products[0] === p.key)
                      ?.name
                  }
                </Typography>
              ) : (
                <>
                  <SelectMenu
                    label="Assigned products"
                    id="products-menu"
                    data-testid="products-menu"
                    className={styles.selectMenu}
                    items={productOptions}
                    onUpdate={async (items) => {
                      const selectMenuItemSingles: SelectMenuItemSingle[] =
                        items.reduce<SelectMenuItemSingle[]>((acc, item) => {
                          if (isSingle(item)) {
                            acc.push(item);
                          }
                          return acc;
                        }, []);
                      const noProductsSelected = selectMenuItemSingles.every(
                        (item) => !item.checked
                      );
                      setValue("assignedProducts", selectMenuItemSingles);

                      if (noProductsSelected) {
                        setError("assignedProducts", {
                          message:
                            "At least one product must be assigned to the company",
                        });
                        return;
                      }
                      clearErrors("assignedProducts");
                      await trigger("assignedProducts");
                    }}
                  />
                  {errors.assignedProducts && (
                    <Typography
                      as="div"
                      variant="body_md"
                      className={styles.error}
                      noMargin
                    >
                      {errors.assignedProducts.message}
                    </Typography>
                  )}
                </>
              )}
            </Modal.Section>
          </Modal.Body>
          <Modal.Footer
            variant="light"
            buttons={[
              {
                label: "Cancel",
                ["data-testid"]: "edit-company-modal-cancel",
                variant: "secondary",
                color: "interface",
                type: "button",
                onClick: () => {
                  sendEvent("Cancel Button - Click");
                  onClose();
                },
              },
              {
                label: "Save",
                isLoading: isPending || isUpdatingProducts,
                disabled: !isValid || !name,
                ["data-testid"]: "edit-company-modal-save",
                onClick: () => {
                  sendEvent("Save Button - Click", { companyId: company.id });
                },
              },
            ]}
          />
        </form>
      </>
    </Modal>
  );
};

export default EditCompanyModal;
